import { AlertFactory } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import BookkeepingProgressionStore from 'jobs/bookkeeping/BookkeepingProgressionStore'
import { action, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { between, maxLength } from 'utils/validation-utils'
import { required, validationContext } from 'validx'

export default class BookkeepingSubmitAssessmentDialogViewModel extends ModalViewModel {
  @observable numberOfBankAccounts = 1
  @observable avgMonthlyExpenses = 0
  @observable cleanup = false
  @observable cleanupReason = ''
  @observable comment = ''

  readonly fieldLengths = {
    numberOfBankAccounts: { min: 1, max: 30 },
    avgMonthlyExpenses: { min: 0, max: 99_999_999 },
    cleanupReason: { min: 3, max: 5_000 },
    comment: { max: 5_000 },
  } as const

  validation = validationContext(this, {
    numberOfBankAccounts: [
      required('The number of bank accounts is required'),
      between(
        this.fieldLengths.numberOfBankAccounts.min,
        this.fieldLengths.numberOfBankAccounts.max,
        `The number of bank accounts must be between ${this.fieldLengths.numberOfBankAccounts.min} and ${this.fieldLengths.numberOfBankAccounts.max}.`,
      ),
    ],
    avgMonthlyExpenses: [
      required('The last 3 month average monthly expenses is required'),
      between(
        this.fieldLengths.avgMonthlyExpenses.min,
        this.fieldLengths.avgMonthlyExpenses.max,
        `The last 3 month average monthly expenses must be between ${this.fieldLengths.avgMonthlyExpenses.min} and ${this.fieldLengths.avgMonthlyExpenses.max}.`,
      ),
    ],
    cleanupReason: [
      (opts) =>
        !this.cleanup ||
        opts.value.length >= this.fieldLengths.cleanupReason.min ||
        `Cleanup reason can't be less than ${this.fieldLengths.cleanupReason.min} characters`,
      (opts) =>
        !this.cleanup ||
        opts.value.length <= this.fieldLengths.cleanupReason.max ||
        `Cleanup reason must not be longer than ${this.fieldLengths.cleanupReason.max}`,
    ],
    comment: [
      maxLength(
        this.fieldLengths.comment.max,
        `Comment must not be longer than ${this.fieldLengths.comment.max}`,
      ),
    ],
  })

  constructor(
    private bookkeepingProgressionStore: BookkeepingProgressionStore,
    private job: Job,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  @action
  reset() {
    this.numberOfBankAccounts = 1
    this.avgMonthlyExpenses = 0
    this.cleanup = false
    this.cleanupReason = ''
    this.comment = ''
  }

  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    try {
      await this.bookkeepingProgressionStore.submitBookkeepingAssessment({
        jobId: this.job.id,
        numberOfBankAccounts: this.numberOfBankAccounts,
        avgMonthlyExpenses: this.avgMonthlyExpenses,
        cleanup: this.cleanup,
        cleanupReason: this.cleanupReason,
        comment: this.comment,
      })
      this.reset()

      this.close()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })
}
